<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('indietro') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-magic me-2 text-primary"></i>
            {{ $t('digitalizzazione_menu') || 'Digitalizzazione Menu con Vision AI' }}
          </h1>
          <p class="sci-subtitle">Converti menu cartacei, foto o file PDF in piatti digitali pronti per la sala</p>
        </div>
      </div>
      <div class="sci-header-right" v-if="extractedItems.length > 0">
        <button class="sci-btn sci-btn-primary" @click="confirmAll" :disabled="isSaving">
          <i class="fas" :class="isSaving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
          <span>{{ isSaving ? 'Importazione in corso...' : 'Conferma e Salva Tutto (' + extractedItems.length + ')' }}</span>
        </button>
      </div>
    </header>

    <!-- Main Spatial Bento Grid -->
    <main class="sci-bento-grid">
      <!-- Left Column: Upload & OCR Studio -->
      <aside class="sci-card upload-panel">
        <div class="sci-card-header">
          <h2 class="sci-card-title">
            <i class="fas fa-cloud-upload-alt me-2 text-primary"></i>
            Caricamento e Opzioni
          </h2>
          <span class="sci-caption">Seleziona le lingue ed effettua l'acquisizione</span>
        </div>

        <div class="upload-body">
          <!-- Language extraction options -->
          <div class="sci-form-group">
            <label class="sci-label">Lingue da Estrarre:</label>
            <div class="lang-chips-grid">
              <label class="lang-chip active locked">
                <input type="checkbox" value="it" v-model="selectedLangs" disabled class="visually-hidden">
                <span class="flag-icon">🇮🇹</span>
                <span class="lang-name">Italiano (Base)</span>
                <i class="fas fa-lock lock-icon"></i>
              </label>
              <label class="lang-chip" :class="{ active: selectedLangs.includes('en') }">
                <input type="checkbox" value="en" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇬🇧</span>
                <span class="lang-name">Inglese (EN)</span>
              </label>
              <label class="lang-chip" :class="{ active: selectedLangs.includes('es') }">
                <input type="checkbox" value="es" v-model="selectedLangs" class="visually-hidden">
                <span class="flag-icon">🇪🇸</span>
                <span class="lang-name">Spagnolo (ES)</span>
              </label>
            </div>
          </div>

          <!-- Hidden inputs for file/camera upload -->
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileUpload" 
            accept=".pdf,image/*" 
            style="display: none" 
          />
          <input 
            type="file" 
            ref="cameraInput" 
            @change="handleFileUpload" 
            accept="image/*" 
            capture="environment" 
            style="display: none" 
          />

          <!-- Action Buttons / Dropzone -->
          <div class="upload-dropzone">
            <div class="dropzone-actions">
              <button 
                class="sci-btn sci-btn-primary w-100 btn-snap"
                @click="$refs.cameraInput.click()" 
                :disabled="isLoading"
              >
                <i class="fas fa-camera me-2"></i>
                Scatta Foto Menu
              </button>

              <button 
                class="sci-btn sci-btn-secondary w-100 btn-upload"
                @click="$refs.fileInput.click()" 
                :disabled="isLoading"
              >
                <i class="fas fa-file-pdf me-2 text-danger"></i>
                Carica PDF o Immagine
              </button>
            </div>
          </div>

          <!-- Progress / Scanning State -->
          <transition name="fade">
            <div v-if="isLoading" class="ai-processing-card">
              <div class="ai-spinner-halo">
                <i class="fas fa-spinner fa-spin"></i>
              </div>
              <h4 class="processing-title">Vision AI al lavoro</h4>
              <p class="processing-msg">{{ loadingMessage }}</p>
              <div class="progress-bar-track">
                <div class="progress-bar-fill"></div>
              </div>
            </div>
          </transition>

          <!-- Tips Card -->
          <div class="ai-info-box">
            <div class="info-header">
              <i class="fas fa-lightbulb text-warning"></i>
              <span class="info-title">Consigli per una scansione ottimale</span>
            </div>
            <ul class="info-list">
              <li>Inquadra il foglio in modo perpendicolare ed evita ombre marcate.</li>
              <li>Per file multipagina, carica direttamente il file PDF del menu.</li>
              <li>Potrai modificare o cancellare qualsiasi piatto prima di salvarlo.</li>
            </ul>
          </div>
        </div>
      </aside>

      <!-- Right Column: Extracted Dishes Review Table -->
      <section class="sci-card results-panel">
        <div class="sci-card-header results-header">
          <div class="results-header-left">
            <h2 class="sci-card-title">
              <i class="fas fa-list-check me-2 text-primary"></i>
              Piatti Estratti dall'AI
            </h2>
            <span class="sci-caption">Verifica i dati riconosciuti, modifica i testi ed i prezzi prima della conferma</span>
          </div>
          <span v-if="extractedItems.length > 0" class="sci-badge sci-badge-count">
            {{ extractedItems.length }} piatti pronti
          </span>
        </div>

        <!-- Empty State -->
        <div v-if="extractedItems.length === 0 && !isLoading" class="sci-empty-state">
          <div class="empty-content">
            <div class="empty-icon-halo">
              <i class="fas fa-camera-retro"></i>
            </div>
            <h3 class="empty-title">Nessun menu caricato</h3>
            <p class="empty-desc">
              Scatta una foto chiara del menu cartaceo oppure carica un file PDF o immagine per avviare il riconoscimento intelligente.
            </p>
          </div>
        </div>

        <!-- Extracted Items Table -->
        <div v-if="extractedItems.length > 0" class="table-responsive">
          <table class="sci-table">
            <thead>
              <tr>
                <th style="min-width: 180px;">Nome (IT)</th>
                <th v-if="selectedLangs.includes('en')" style="min-width: 160px;">Nome (EN)</th>
                <th v-if="selectedLangs.includes('es')" style="min-width: 160px;">Nome (ES)</th>
                <th style="width: 130px;">Prezzo (€)</th>
                <th>Descrizione / Ingredienti</th>
                <th style="width: 60px; text-align: center;">Rimuovi</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in extractedItems" :key="index" class="sci-table-row">
                <!-- Name IT -->
                <td>
                  <input 
                    type="text" 
                    v-model="item.nome" 
                    class="sci-table-input font-bold"
                    placeholder="Nome piatto (IT)..."
                  />
                </td>

                <!-- Name EN -->
                <td v-if="selectedLangs.includes('en')">
                  <input 
                    type="text" 
                    v-model="item.nomeEn" 
                    class="sci-table-input"
                    placeholder="Nome inglese (EN)..."
                  />
                </td>

                <!-- Name ES -->
                <td v-if="selectedLangs.includes('es')">
                  <input 
                    type="text" 
                    v-model="item.nomeEs" 
                    class="sci-table-input"
                    placeholder="Nome spagnolo (ES)..."
                  />
                </td>

                <!-- Price -->
                <td>
                  <div class="sci-currency-field">
                    <span class="currency-symbol">€</span>
                    <input 
                      type="number" 
                      step="0.10" 
                      min="0"
                      v-model.number="item.prezzo" 
                      class="sci-currency-input font-monospace"
                    />
                  </div>
                </td>

                <!-- Description -->
                <td>
                  <textarea 
                    v-model="item.descrizione" 
                    class="sci-table-textarea"
                    rows="2"
                    placeholder="Descrizione ed ingredienti..."
                  ></textarea>
                </td>

                <!-- Remove action -->
                <td style="text-align: center;">
                  <button 
                    class="btn-delete-row" 
                    title="Rimuovi piatto"
                    @click="removeItem(index)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Digitalizzazione',
  data() {
    return {
      store,
      selectedLangs: ['it'],
      isLoading: false,
      isSaving: false,
      loadingMessage: '',
      digitizationId: null,
      extractedItems: [],
      pollingInterval: null
    }
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.isLoading = true;
      this.loadingMessage = 'Caricamento file al server in corso...';
      this.extractedItems = [];

      const formData = new FormData();
      formData.append('file', file);
      formData.append('languages', JSON.stringify(this.selectedLangs));

      try {
        const response = await this.axios.post(this.store.path + 'digitizzazione/upload', formData, {
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        });

        this.digitizationId = response.data.digitizationId;
        this.startPolling();
      } catch (error) {
        console.error('Upload error:', error);
        alert('Errore durante il caricamento del file.');
        this.isLoading = false;
      }
    },
    startPolling() {
      this.loadingMessage = 'Vision AI: Estrazione piatti, prezzi e categorie in corso...';
      this.pollingInterval = setInterval(async () => {
        try {
          const response = await this.axios.get(this.store.path + 'digitizzazione/status/' + this.digitizationId, {
            headers: {
              authorization: `Bearer ${JSON.parse(localStorage.token)}`
            }
          });

          if (response.data.stato === 'PROCESSED') {
            this.stopPolling();
            this.extractedItems = response.data.items || [];
            this.isLoading = false;
          } else if (response.data.stato === 'ERROR') {
            this.stopPolling();
            alert('Errore durante l\'elaborazione AI del menu.');
            this.isLoading = false;
          }
        } catch (error) {
          console.error('Polling error:', error);
          this.stopPolling();
          this.isLoading = false;
        }
      }, 3000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    removeItem(index) {
      this.extractedItems.splice(index, 1);
    },
    async confirmAll() {
      if (this.extractedItems.length === 0) return;
      
      this.isSaving = true;
      try {
        await this.axios.post(this.store.path + 'digitizzazione/confirm', {
          digitizationId: this.digitizationId,
          items: this.extractedItems
        }, {
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        });

        alert('Menu salvato correttamente con tutti i piatti estratti!');
        this.$router.push({ name: 'EditMenu' });
      } catch (error) {
        console.error('Confirm error:', error);
        alert('Errore durante il salvataggio del menu.');
      } finally {
        this.isSaving = false;
      }
    }
  },
  beforeUnmount() {
    this.stopPolling();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-badge-count {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* Bento Grid */
.sci-bento-grid {
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 24px;
  max-width: 1400px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
}

@media (max-width: 960px) {
  .sci-bento-grid {
    grid-template-columns: 1fr;
  }
}

/* Bento Cards */
.sci-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.sci-card-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.sci-card-title {
  margin: 0 0 4px 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.sci-caption {
  font-size: 0.85rem;
  color: #64748b;
}

/* Left Panel */
.upload-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.sci-form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sci-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.lang-chips-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.lang-chip {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 10px 16px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  position: relative;
}

.lang-chip:hover:not(.locked) {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.lang-chip.active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #1d4ed8;
  font-weight: 600;
}

.lang-chip.locked {
  cursor: default;
  background: #f1f5f9;
}

.flag-icon {
  font-size: 1.25rem;
}

.lang-name {
  font-size: 0.95rem;
}

.lock-icon {
  margin-left: auto;
  font-size: 0.8rem;
  color: #94a3b8;
}

/* Upload Dropzone */
.upload-dropzone {
  border: 2px dashed #cbd5e1;
  border-radius: 14px;
  padding: 20px;
  background: #fafbfc;
  transition: border-color 0.2s ease;
}

.upload-dropzone:hover {
  border-color: #3b82f6;
}

.dropzone-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-snap {
  min-height: 48px;
  font-size: 1rem;
}

.btn-upload {
  min-height: 48px;
  font-size: 0.95rem;
}

/* AI Processing Card */
.ai-processing-card {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.ai-spinner-halo {
  font-size: 2rem;
  color: #2563eb;
  margin-bottom: 12px;
}

.processing-title {
  margin: 0 0 4px 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e40af;
}

.processing-msg {
  margin: 0 0 14px 0;
  font-size: 0.85rem;
  color: #3b82f6;
}

.progress-bar-track {
  height: 6px;
  background: #dbeafe;
  border-radius: 6px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  width: 60%;
  background: #2563eb;
  border-radius: 6px;
  animation: progressPulse 1.8s infinite ease-in-out alternate;
}

@keyframes progressPulse {
  0% { width: 15%; margin-left: 0; }
  50% { width: 50%; margin-left: 25%; }
  100% { width: 80%; margin-left: 20%; }
}

/* AI Info Box */
.ai-info-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.info-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #334155;
}

.info-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.5;
}

/* Right Panel: Results */
.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Table */
.sci-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.sci-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 14px 18px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sci-table-row td {
  padding: 12px 18px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.sci-table-input {
  width: 100%;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.92rem;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.2s;
}

.sci-table-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.font-bold {
  font-weight: 600;
}

.sci-currency-field {
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 110px;
}

.currency-symbol {
  position: absolute;
  left: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  pointer-events: none;
}

.sci-currency-input {
  width: 100%;
  min-height: 40px;
  padding: 8px 8px 8px 26px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;
  outline: none;
}

.sci-currency-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.font-monospace {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
}

.sci-table-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #334155;
  resize: vertical;
  min-height: 44px;
}

.sci-table-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.btn-delete-row {
  background: none;
  border: 1px solid #fecaca;
  color: #ef4444;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-row:hover {
  background: #fee2e2;
  color: #b91c1c;
}

/* Empty State */
.sci-empty-state {
  padding: 80px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 440px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: 18px;
}

.empty-title {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.92rem;
  color: #64748b;
  line-height: 1.5;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-primary {
  background: #10b981;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

.sci-btn-primary:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.sci-btn-secondary {
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

.sci-btn:disabled {
  background: #cbd5e1;
  color: #94a3b8;
  cursor: not-allowed;
  box-shadow: none;
}
</style>
